import type { TimelineChange, TimelineEntry } from '@zealot/ui/components/activity/timeline-entry';
import { Badge } from '@zealot/ui/components/badge';
import { HStack } from '@zealot/ui/components/hstack';
import { Text } from '@zealot/ui/components/text';
import type { ReactNode } from 'react';

type TimelineEventRowProps = {
  entry: TimelineEntry;
  action?: ReactNode;
};

// gloss
const ChangeLine = ({ change, showLabel }: { change: TimelineChange; showLabel: boolean }) => (
  <li className="text-xs" data-slot="activity-timeline-change">
    {showLabel && change.label && (
      <span className="mr-1 text-muted-foreground after:content-[':']">{change.label}</span>
    )}
    {change.from && (
      <>
        <span className="text-muted-foreground">{change.from}</span>
        <span className="mx-1 text-muted-foreground">→</span>
      </>
    )}
    {change.to && <span className="font-medium text-foreground">{change.to}</span>}
  </li>
);

// gloss
export const TimelineEventRow = ({ entry, action }: TimelineEventRowProps) => {
  const meta = [entry.actorLabel, entry.timestamp].filter(Boolean).join(' · ');
  const changes = entry.changes ?? [];

  return (
    <>
      <div className="min-w-0 flex-1 pb-4">
        <HStack gap={2} align="center" className="flex-wrap">
          {}
          <Text size="sm" weight="medium" className="whitespace-pre-wrap break-words">
            {entry.title}
          </Text>
          {entry.badge && (
            <Badge variant={entry.badge.variant ?? 'outline'} className="uppercase tracking-wide">
              {entry.badge.label}
            </Badge>
          )}
        </HStack>
        {entry.quote && (
          <blockquote
            className="mt-1.5 border-primary border-l-2 bg-muted/30 py-1.5 pr-2 pl-3 text-muted-foreground text-xs italic"
            data-slot="activity-timeline-quote"
          >
            {entry.quote}
          </blockquote>
        )}
        {entry.secondary && <span className="mt-0.5 block text-muted-foreground text-xs">{entry.secondary}</span>}
        {changes.length > 0 && (
          <ul className={changes.length > 1 ? 'mt-1 list-inside list-disc space-y-0.5' : 'mt-0.5'}>
            {changes.map((change) => (
              <ChangeLine
                key={change.label ?? change.to ?? change.from}
                change={change}
                showLabel={changes.length > 1}
              />
            ))}
          </ul>
        )}
        {meta && <span className="mt-0.5 block text-muted-foreground text-xs">{meta}</span>}
      </div>
      {action && (
        <div className="shrink-0" data-slot="activity-timeline-entry-action">
          {action}
        </div>
      )}
    </>
  );
};
